<template>
  <header class="header-actions">
    <div class="header-actions__container">
      <CChannelHighlight
        v-if="showHighlight"
        channels-header
        tooltip-position="bottom-start"
        size="small"
        :channel-id="currentChannel.id"
        :is-highlighted="highlight"
      />

      <CMemberChannelsDropdown :channel="currentChannel" />

      <nav
        v-if="hideActions"
        class="header-actions__nav"
        name="header-actions"
        aria-label="header-actions"
      >
        <CHealthcareTeam
          :team="currentChannel.staffArray"
          :tooltip-position="hideActions ? 'bottom-end' : 'bottom'"
          class="header-actions__team"
          limit
        />
      </nav>

      <nav v-else class="header-actions__nav" aria-label="header-actions">
        <CHeaderButton
          v-if="currentChannel.isActive"
          title="Configurações"
          icon="icSettings"
          @click="toggleChannelControl"
        />

        <CHeaderButton
          v-if="isActive"
          title="Criar nova consulta"
          icon="icAddNote"
          @click="newClinicalRecord"
        />

        <HDropdown
          id="dropdown-actions"
          option-key="name"
          icon="icMenuKebab"
          class="header-actions__config"
          :options="menuOptions"
          position="right"
          size="small"
        >
          <template #item="{ item }">
            <WHelper
              v-if="item.isDivider"
              class="header-actions__config__dropdown--divider"
            >
              {{ item.name }}
            </WHelper>
            <div v-else class="header-actions__config__dropdown">
              <WIcon :icon="item.icon" />
              <span>{{ item.name }}</span>
            </div>
          </template>
        </HDropdown>
      </nav>
    </div>
    <CHeaderTags
      :health-tags="healthTags"
      :channel="currentChannel"
      :last-sync="channelLastSync"
    />
  </header>
</template>

<script>
import { WHelper, WIcon } from '@alice-health/wonderland-vue'

import { CHeaderButton, CHealthcareTeam } from '@/components/channels/atoms'
import { HDropdown } from '@/components/shared/atoms'
import {
  CHANNEL_CATEGORIES,
  CHANNEL_KINDS,
  CHANNEL_SUBCATEGORIES
} from '@/models/channels'
import { CHANNELS_MENU } from '@/models/menu'
import Logger, { EventModel, EventName } from '@/wrappers/logger'

import { CChannelHighlight } from '../'
import CHeaderTags from './CHeaderTags'
import CMemberChannelsDropdown from './CMemberChannelsDropdown'

export default {
  components: {
    CHealthcareTeam,
    CChannelHighlight,
    CMemberChannelsDropdown,
    CHeaderButton,
    CHeaderTags,
    WIcon,
    WHelper,
    HDropdown
  },
  props: {
    hideActions: Boolean,
    userId: { type: String, default: undefined },
    currentChannel: { type: Object, default: undefined },
    healthTags: { type: Array, default: () => [] },
    isChimeVideoCallTester: Boolean,
    isN2RedirectionEnabled: Boolean
  },
  emits: [
    'onShowSearch',
    'onShowHandoff',
    'onShowChannelControl',
    'onStartVideoCall',
    'onRedirectChat',
    'onAddClinicalRecord',
    'onRedirectToN2'
  ],
  computed: {
    showRedirectVirtualClinic() {
      return (
        this.currentChannel?.subCategory === CHANNEL_SUBCATEGORIES.SCREENING
      )
    },
    isChat() {
      return this.currentChannel.kind === CHANNEL_KINDS.CHAT
    },
    isChannel() {
      return this.currentChannel.kind === CHANNEL_KINDS.CHANNEL
    },
    shoudShowN2RedirectOption() {
      return (
        this.isChannel &&
        this.isN2RedirectionEnabled &&
        (this.currentChannel.category === CHANNEL_CATEGORIES.ADMINISTRATIVE ||
          this.currentChannel.subCategory ===
            CHANNEL_SUBCATEGORIES.LONGITUDINAL) &&
        !this.currentChannel.tags?.includes('aguardando_n2') &&
        !this.currentChannel.tags?.includes('redirecionado_n2')
      )
    },
    isActive() {
      return this.currentChannel.isActive
    },
    menuOptions() {
      const options = [
        {
          name: 'Busca',
          icon: 'icSearch',
          onClick: this.showSearchMessage
        }
      ]

      const videoMenu = this.getVideoMenu()
      if (videoMenu) {
        options.push(videoMenu)
      }

      const handoffMenu = this.getHandoffMenu()
      if (handoffMenu) {
        options.push(handoffMenu)
      }

      const redirects = this.getRedirectMenu()
      if (redirects?.length) {
        options.push({ name: 'Redirecionar', isDivider: true })
        options.push(...redirects)
      }

      return options
    },
    showHighlight() {
      return (
        this.currentChannel.isActive &&
        this.currentChannel.name &&
        !this.hideActions
      )
    },
    channelLastSync() {
      if (!this.userId || !this.currentChannel?.staff) {
        return null
      }
      const staffLastSync = this.currentChannel.staff[this.userId]?.lastSync
      if (staffLastSync) {
        return new Date(staffLastSync.seconds * 1000)
      }
      return null
    },
    highlight() {
      return this.currentChannel.staff[this.userId]?.highlight
    }
  },
  methods: {
    getRedirectMenu() {
      const menus = []

      if (this.isChat) {
        /**
               Canal => pode ser redirecionado para
               SCREENING => ADM || PA Virtual
               ADM => SCREENING
               Longitudinal => SCREENING || ADM
             */
        if (
          this.currentChannel.category === CHANNEL_CATEGORIES.ADMINISTRATIVE ||
          this.currentChannel.subCategory === CHANNEL_SUBCATEGORIES.LONGITUDINAL
        ) {
          const redirectLabel = 'Enfermeiro(a) Triagem'
          menus.push(
            this.createDropdownLink(redirectLabel, 'icRepeat', () =>
              this.redirectChat({
                category: CHANNEL_CATEGORIES.ASSISTANCE,
                subCategory: CHANNEL_SUBCATEGORIES.SCREENING,
                redirectLabel
              })
            )
          )
        }
        if (
          this.currentChannel.subCategory === CHANNEL_SUBCATEGORIES.SCREENING ||
          this.currentChannel.subCategory === CHANNEL_SUBCATEGORIES.LONGITUDINAL
        ) {
          const redirectLabel = 'MemberX'
          menus.push(
            this.createDropdownLink('MemberX', 'icRepeat', () =>
              this.redirectChat({
                category: CHANNEL_CATEGORIES.ADMINISTRATIVE,
                subCategory: null,
                reasonRequired: true,
                redirectLabel
              })
            )
          )
        }
        if (
          this.currentChannel.subCategory === CHANNEL_SUBCATEGORIES.SCREENING
        ) {
          const redirectLabel = 'PA Virtual'
          menus.push(
            this.createDropdownLink(redirectLabel, 'icVideoOn', () =>
              this.redirectChat({
                category: CHANNEL_CATEGORIES.ASSISTANCE,
                subCategory: CHANNEL_SUBCATEGORIES.VIRTUAL_CLINIC,
                redirectLabel,
                validateRedirect: currentChannel => {
                  if (!currentChannel?.appointmentIds?.length) {
                    return 'Você precisa criar um registro clínico antes de enviar o membro para o PA digital'
                  }
                }
              })
            )
          )
        }
      }

      if (this.shoudShowN2RedirectOption) {
        menus.push(
          this.createDropdownLink('N2', 'icRepeat', () => this.redirectToN2())
        )
      }

      return menus
    },
    createDropdownLink(name, icon, onClick) {
      return {
        name,
        icon,
        onClick
      }
    },
    getVideoMenu() {
      if (this.isChimeVideoCallTester) {
        return {
          name: 'Começar uma chamada de vídeo',
          icon: 'icVideoOn',
          onClick: this.startVideoCall
        }
      }
    },
    getHandoffMenu() {
      return {
        name: 'Plano e comentários',
        icon: 'icPaper',
        onClick: this.toggleHandoff
      }
    },
    showSearchMessage() {
      Logger.addLog(EventName.USER_INTERACTION_MENU, {
        model: EventModel.CHANNELS,
        data: {
          context: CHANNELS_MENU.OPEN_SEARCH
        }
      })
      this.$emit('onShowSearch')
    },
    toggleHandoff() {
      Logger.addLog(EventName.USER_INTERACTION_MENU, {
        model: EventModel.CHANNELS,
        data: {
          context: CHANNELS_MENU.TOGGLE_HANDOFF
        }
      })
      this.$emit('onShowHandoff')
    },
    toggleChannelControl() {
      Logger.addLog(EventName.USER_INTERACTION_MENU, {
        model: EventModel.CHANNELS,
        data: {
          context: CHANNELS_MENU.SHOW_CONFIG
        }
      })
      this.$emit('onShowChannelControl')
    },
    startVideoCall() {
      Logger.addLog(EventName.USER_INTERACTION_MENU, {
        model: EventModel.CHANNELS,
        data: {
          context: CHANNELS_MENU.START_VIDEOCALL
        }
      })
      this.$emit('onStartVideoCall')
    },
    redirectChat(data) {
      Logger.addLog(EventName.USER_INTERACTION_MENU, {
        model: EventModel.CHANNELS,
        data: {
          context: CHANNELS_MENU.REDIRECT_CHAT,
          value: { ...data }
        }
      })
      this.$emit('onRedirectChat', data)
    },
    redirectToN2(data) {
      Logger.addLog(EventName.USER_INTERACTION_MENU, {
        model: EventModel.CHANNELS,
        data: {
          context: CHANNELS_MENU.REDIRECT_TO_N2,
          value: { ...data }
        }
      })
      this.$emit('onRedirectToN2', data)
    },
    newClinicalRecord() {
      Logger.addLog(EventName.USER_INTERACTION_MENU, {
        model: EventModel.CHANNELS,
        data: {
          context: CHANNELS_MENU.NEW_CLINICAL_RECORD
        }
      })
      this.$emit('onAddClinicalRecord')
    }
  }
}
</script>

<style lang="scss" scoped>
.header-actions {
  min-height: var(--filter-header-height);
  padding-top: var(--gl-spacing-03);
  padding-bottom: var(--gl-spacing-03);

  &__container {
    display: flex;
    align-items: center;
  }

  &__team {
    margin-left: auto;
    padding: 0 var(--gl-spacing-04);
  }
  &__nav {
    display: flex;
    gap: var(--gl-spacing-01);
    margin-left: auto;
  }

  &__config {
    &__dropdown {
      display: flex;
      align-items: center;
      gap: var(--gl-spacing-02);

      &--divider {
        color: var(--sys-color-content-tertiary);
      }
    }
  }
}
</style>
